import type { ErrorEvent } from '@sentry/react';

// What WebKit, then Chromium and Gecko, say when a dynamic import fails.
// WebKit says the same when the module fails to evaluate, and names no URL.
const MODULE_IMPORT_ERROR =
  /^(?:Importing a module script failed\.$|Failed to fetch dynamically imported module: |error loading dynamically imported module: )/;

// A script of this app's build, which every deploy serves under `/apply/<sha7>/`.
const BUNDLE_SCRIPT = /^\/apply\/[0-9a-f]{7}\/[^/]+\.js$/;

// Long enough for a slow mobile network, short enough that the event still
// leaves before the candidate closes the tab.
const PROBE_TIMEOUT_MS = 5000;

/** Whether the event reports a dynamic import that failed. */
export const isModuleImportError = (event: ErrorEvent): boolean => {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  return MODULE_IMPORT_ERROR.test(exception?.value ?? '');
};

/**
 * The paths of the bundle scripts the page has requested.
 *
 * Vite preloads a dynamic import's own chunk, with its dependencies, through a
 * `modulepreload` link before it imports it, so the chunk that failed is among
 * these.
 */
const bundleScriptPaths = (): string[] => {
  const urls = [
    ...Array.from(
      document.querySelectorAll<HTMLScriptElement>(
        'script[type="module"][src]',
      ),
      script => script.src,
    ),
    ...Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"]'),
      link => link.href,
    ),
  ];
  const paths = urls.map(url => new URL(url, window.location.href).pathname);

  return [...new Set(paths)].filter(path => BUNDLE_SCRIPT.test(path));
};

type ChunkResult = { path: string; status: string };

/**
 * What the network serves for a bundle script now, or `null` when it is
 * served as JavaScript.
 */
const probeChunk = async (
  path: string,
  signal: AbortSignal,
): Promise<ChunkResult | null> => {
  try {
    const response = await fetch(path, { signal });
    const contentType = response.headers.get('content-type') ?? '';

    if (response.ok && contentType.includes('javascript')) return null;

    return { path, status: `${response.status} ${contentType}`.trim() };
  } catch {
    return { path, status: signal.aborted ? 'timeout' : 'network-error' };
  }
};

/**
 * Adds to a failed dynamic import's event what the network serves for each
 * bundle script.
 *
 * The browser does not say which chunk failed or why, so this requests every
 * bundle script the page asked for again. The `chunks` context lists each one
 * not served as JavaScript, with its HTTP status and content type, a timeout,
 * or a network error. The `chunk.status` tag holds the first such status, or
 * `ok` when every script is served as JavaScript: a chunk that failed to
 * evaluate, or a failure the network has since stopped returning. The paths
 * hold only the build sha and file hashes.
 */
export async function withChunkProbe(event: ErrorEvent): Promise<ErrorEvent> {
  const paths = bundleScriptPaths();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const results = await Promise.all(
      paths.map(path => probeChunk(path, controller.signal)),
    );
    const failed = results.filter(
      (result): result is ChunkResult => result !== null,
    );

    return {
      ...event,
      tags: { ...event.tags, 'chunk.status': failed[0]?.status ?? 'ok' },
      contexts: {
        ...event.contexts,
        chunks: { checked: paths.length, failed },
      },
    };
  } finally {
    clearTimeout(timer);
  }
}
